import { produce } from "immer";
import { sortBy } from "lodash";
import React, { useContext, useEffect, useState } from "react";

import { gql } from "@examspark/api";
import { filterMaybeArray } from "@examspark/common";
import { CountOption, Form, SelectInput, VSpace } from "@examspark/common-ui";
import { BucketAggregation, Objective } from "@examspark/types";

import { NewActivityContext } from "../../../../classroom";
import { useExamBuilderSearch } from "../hooks/useSearch";

const SubjectGradeSelect = (props: { objectiveAuthorityId: string }) => {
  const [state, dispatchState] = useContext(NewActivityContext);
  const search = useExamBuilderSearch();
  const [documents, setDocuments] = useState<any[]>();
  const [sections, setSections] = useState<Objective[]>();
  const [sectionFacets, setSectionFacets] = useState<BucketAggregation[]>();
  const authorityId = props.objectiveAuthorityId;

  /** Responsible for displaying list of subjects/revision list */
  const fetchDocuments = async () => {
    // if (!context.app) return;
    const searchResults = await search.searchItems({
      exclude: [
        "document",
        "section",
        "passages",
        "interactionTypes",
        "objectives",
      ],
    });

    const facets = searchResults?.aggregations?.objectiveDocument?.buckets;
    // console.log("facets", objDocumentFacetValues);

    const documentOptions: {
      value: string;
      label: string;
      count: number;
    }[] = [];

    if (facets) {
      await Promise.all(
        filterMaybeArray<BucketAggregation>(facets).map(async (f) => {
          const result = await gql.operations.objective.getObjectiveDocument(
            authorityId,
            f.key,
          );
          if (!result) return;

          documentOptions.push({
            value: result.sk,
            label: result.content || "",
            count: f.doc_count || 0,
          });
        }),
      );
    }

    setDocuments(documentOptions);
  };

  /** Fetch grade level/course information */
  const fetchSections = async () => {
    if (!state.document) return;
    const result = await gql.operations.objective.listObjectiveSections(
      authorityId,
      state.document.replace("document#", "section#"),
    );
    setSections(result?.items || []);

    const searchResults = await search.searchItems({
      exclude: ["section", "passages", "interactionTypes"],
    });
    setSectionFacets(
      searchResults?.aggregations?.objectiveScope?.buckets || [],
    );
  };

  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorityId]);

  useEffect(() => {
    if (state.document) {
      fetchSections();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.document]);

  return (
    <VSpace>
      {/* <Toolbar>
        <button
          onClick={() => {
            fetchDocuments();
          }}
        >
          refresh docs
        </button>
        <button
          onClick={() => {
            fetchSections();
          }}
        >
          refresh secs
        </button>
      </Toolbar> */}

      <Form.Field label="Content Subject">
        <SelectInput.Simple
          placeholder="Select Subject..."
          value={state.document}
          components={{ Option: CountOption as any }}
          onChange={(value) => {
            // console.log("changed", value);
            dispatchState({
              type: "UPDATE",
              data: produce(state, (draft) => {
                draft.document = value;
                draft.section = undefined;
                draft.sections = [];
                draft.language = undefined;
                draft.objectives = [];
                draft.passages = [];
              }),
            });
          }}
          options={sortBy(
            documents?.reduce<any[]>((state, doc) => {
              const year = doc.value.split("#")[3].split(":")[1];
              state.push({
                value: doc.value,
                label: `${doc.label} (${year})`,
                count: doc.count,
              });

              return state;
            }, []) || [],
            ["label"],
          )}
        />
      </Form.Field>
      <Form.Field label="Content Grade/Course">
        <SelectInput
          isMulti
          value={state.sections}
          disabled={!state.document}
          placeholder="Select Grade..."
          onChange={(values) => {
            if (!values) return;
            const sortedValues = [...values.map((v) => v.value)].sort();
            dispatchState({
              type: "UPDATE",
              data: produce(state, (draft) => {
                draft.sections = sortedValues;
                draft.language = undefined;
                draft.objectives = [];
                draft.passages = [];
              }),
            });
          }}
          components={{ Option: CountOption as any }}
          options={
            sections?.reduce<any[]>((state, sec) => {
              const facet = sectionFacets?.find(
                (f) => f.key === sec.sk.replace("section#", ""),
              );
              const count = facet?.doc_count || 0;
              if (!count) return state;

              state.push({
                value: sec.sk,
                label: `${sec.content}`,
                count: count,
              });

              return state;
            }, []) || []
          }
        />
      </Form.Field>
    </VSpace>
  );
};

export default SubjectGradeSelect;
